import type { FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import type { BookingPolicy } from "../../../../common/src";
import { bookingPolicySchema } from "../../../../common/src";
import { useAction } from "../../hooks/useAction";
import { useAsyncData } from "../../hooks/useAsyncData";
import { trpc } from "../../trpc";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { LoadError, Loading } from "../shared/StatusViews";
import { useFormStyles } from "../shared/styles";

/** Site-wide rules that apply to each person across all boats. */
export const BookingPolicyCard = () => {
  const form = useFormStyles();
  const load = useCallback(
    (signal: AbortSignal) =>
      trpc.bookingPolicy.get.query(undefined, { signal }),
    []
  );
  const policy = useAsyncData(load);
  const [limit, setLimit] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const save = useAction(
    useCallback(
      (input: BookingPolicy) => trpc.bookingPolicy.update.mutate(input),
      []
    )
  );

  const loadedLimit = policy.data?.maxUpcomingReservationsPerPerson;
  useEffect(() => {
    if (loadedLimit !== undefined) {
      setLimit(String(loadedLimit));
    }
  }, [loadedLimit]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const parsed = bookingPolicySchema.safeParse({
      maxUpcomingReservationsPerPerson:
        limit.trim() === "" ? Number.NaN : Number(limit),
    });
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message);
      return;
    }
    setFieldError(undefined);
    if (await save.run(parsed.data)) {
      setSaved(true);
    }
  };

  return (
    <Card title="Booking policy">
      {policy.error && (
        <LoadError message={policy.error} onRetry={policy.reload} />
      )}
      {policy.loading && !policy.data && <Loading />}
      {policy.data && (
        <form onSubmit={(e) => void submit(e)} noValidate>
          <div className={form.grid}>
            <Field
              label="Upcoming reservations per person"
              htmlFor="policy-limit"
              hint="Counted across all boats. People also can't book overlapping trips."
              error={fieldError}
            >
              <input
                id="policy-limit"
                type="number"
                min={1}
                max={20}
                className={form.control}
                value={limit}
                onChange={(e) => {
                  setSaved(false);
                  setLimit(e.target.value);
                }}
              />
            </Field>
          </div>
          {save.error && <Notice kind="error">{save.error}</Notice>}
          {saved && <Notice kind="success">Booking policy saved.</Notice>}
          <div className={form.actions}>
            <Button type="submit" disabled={save.pending}>
              {save.pending ? "Saving…" : "Save policy"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
};
